import { useEffect, useState } from "react";
import {
  Copy,
  Globe,
  Lock,
  Link as LinkIcon,
  Trash2,
  Users,
} from "lucide-react";
import { useApi, patch, post } from "./api";
import { Avatar, Modal } from "./ui";
import type { Board, Member } from "../shared/model";
type Sharing = {
  teamRole: "viewer" | "editor" | null;
  grants: {
    user_id: string;
    role: "viewer" | "editor";
    name: string;
    email: string;
  }[];
  links: { hash: string; created_at: string }[];
};
export function ShareDialog({
  board,
  onClose,
}: {
  board: Board;
  onClose: () => void;
}) {
  const api = useApi(),
    [data, setData] = useState<Sharing>(),
    [members, setMembers] = useState<Member[]>([]),
    [selected, setSelected] = useState(""),
    [role, setRole] = useState("editor"),
    [url, setUrl] = useState(""),
    [error, setError] = useState(""),
    [copied, setCopied] = useState(false);
  const load = () =>
    api.request<Sharing>(`/api/boards/${board.id}/share`).then(setData);
  useEffect(() => {
    void Promise.all([
      load(),
      api.request<Member[]>("/api/members").then(setMembers),
    ]).catch((e) => setError(e.message));
  }, []);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      setError("");
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Modal
      title={`Share ${board.title}`}
      description="A little collaboration goes a long way."
      onClose={onClose}
    >
      <div className="share-section">
        <h3>
          <Users size={17} />
          Invite teammates
        </h3>
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() =>
              api.request(
                `/api/boards/${board.id}/share`,
                patch({ userId: selected, role }),
              ),
            ).then(() => setSelected(""));
          }}
        >
          <select
            aria-label="Choose teammate"
            value={selected}
            required
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Choose a teammate…</option>
            {members
              .filter(
                (m) =>
                  m.id !== board.owner_id &&
                  !data?.grants.some((g) => g.user_id === m.id),
              )
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
          <select
            aria-label="Invitation permission"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="editor">Can edit</option>
            <option value="viewer">Can view</option>
          </select>
          <button className="button primary">Add</button>
        </form>
        <div className="member-list">
          {data?.grants.map((g) => (
            <div className="member-row" key={g.user_id}>
              <Avatar name={g.name} />
              <div>
                <b>{g.name}</b>
                <small>{g.email}</small>
              </div>
              <select
                aria-label={`Permission for ${g.name}`}
                value={g.role}
                onChange={(e) =>
                  void run(() =>
                    api.request(
                      `/api/boards/${board.id}/share`,
                      patch({
                        userId: g.user_id,
                        role:
                          e.target.value === "remove" ? null : e.target.value,
                      }),
                    ),
                  )
                }
              >
                <option value="editor">Can edit</option>
                <option value="viewer">Can view</option>
                <option value="remove">Remove</option>
              </select>
            </div>
          ))}
        </div>
      </div>
      <div className="share-section">
        <h3>
          <Lock size={17} />
          Workspace access
        </h3>
        <div className="access-row">
          <div>
            <b>Renn Media</b>
            <small>
              {data?.teamRole
                ? "Everyone in your workspace can access this board."
                : "Only invited people and workspace admins have access."}
            </small>
          </div>
          <select
            aria-label="Workspace access"
            value={data?.teamRole || "private"}
            onChange={(e) =>
              void run(() =>
                api.request(
                  `/api/boards/${board.id}/share`,
                  patch({
                    teamRole:
                      e.target.value === "private" ? null : e.target.value,
                  }),
                ),
              )
            }
          >
            <option value="private">Private</option>
            <option value="viewer">Can view</option>
            <option value="editor">Can edit</option>
          </select>
        </div>
      </div>
      <div className="share-section">
        <h3>
          <Globe size={17} />
          Public view-only links
        </h3>
        <p className="muted">
          Anyone with an active link can view this board. Editing requires a
          Renn Media account and permission.
        </p>
        <button
          className="button secondary"
          onClick={() =>
            void run(async () => {
              const result = await api.request<{ url: string }>(
                `/api/boards/${board.id}/links`,
                post(),
              );
              setUrl(result.url);
              setCopied(false);
            })
          }
        >
          <LinkIcon size={16} />
          Create a view link
        </button>
        {url && (
          <div className="copy-field">
            <input aria-label="Public board link" readOnly value={url} />
            <button
              className="icon-button"
              aria-label="Copy public link"
              onClick={() =>
                void navigator.clipboard
                  .writeText(url)
                  .then(() => setCopied(true))
              }
            >
              <Copy size={17} />
            </button>
          </div>
        )}
        {copied && <p className="success-text">Link copied</p>}
        {data?.links.map((link, i) => (
          <div className="link-row" key={link.hash}>
            <LinkIcon size={16} />
            <span>
              View link {i + 1}
              <small>
                Created {new Date(link.created_at).toLocaleDateString()}
              </small>
            </span>
            <button
              className="button text-button danger"
              onClick={() =>
                void run(async () => {
                  await api.request(`/api/boards/${board.id}/links`, {
                    method: "DELETE",
                    body: JSON.stringify({ hash: link.hash }),
                  });
                  setUrl("");
                  setCopied(false);
                })
              }
            >
              <Trash2 size={15} />
              Revoke
            </button>
          </div>
        ))}
      </div>
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      <div className="dialog-footer">
        <button className="button primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}
